import React, { useContext, useEffect, useState } from 'react';

import SVG from 'react-inlinesvg';
import { when } from 'mobx';
import { observer } from 'mobx-react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { Col, ControlLabel, Form, FormGroup, Row } from 'react-bootstrap';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Grid,
  GridItem,
  MenuToggle,
  MenuToggleElement,
} from '@patternfly/react-core';
import { storesContext } from 'stores';
import UsersIcon from 'img/icons/users.svg';
import UserIcon from 'img/icons/single-user.svg';

import { USER_SUBSCRIPTION_CODE } from 'config/constants';
import ViewLoader from 'components/Loading/View';
import { Modality } from 'types';
import './styles.scss';

interface OrgLeaderCustomPathSelectionProps {
  isSubscriptionsLoading?: boolean;
  onSelectMyTeam?: (subscriptionCode: string | null) => void;
  onSelectMyself?: () => void;
  subscriptionCode?: string;
  subscriptions?: string[];
}

const OrgLeaderCustomPathSelection = ({
  isSubscriptionsLoading = true,
  onSelectMyTeam = () => {},
  onSelectMyself = () => {},
  subscriptionCode,
  subscriptions = [],
}: OrgLeaderCustomPathSelectionProps) => {
  const { t } = useTranslation();

  const { catalogStore, routerStore, searchStore, userStore } =
    useContext(storesContext);

  const [catalogUpdateInProgress, setCatalogUpdateInProgress] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<string>(subscriptionCode);

  useEffect(() => {
    when(
      () => !!subscriptionCode,
      () => {
        setSelectedSubscription(subscriptionCode);
      },
    );
  }, [subscriptionCode]);

  const presetFilters = () => {
    catalogStore.clearFilters();
    if ((routerStore?.route as any)?.params?.q) {
      searchStore.query = (routerStore?.route as any)?.params?.q;
    } else {
      searchStore.query = '';
    }
    catalogStore.presetFilters();
  };

  const handleCreateForMyTeam = async () => {
    if (catalogUpdateInProgress) return;

    setCatalogUpdateInProgress(true);
    const entries = await catalogStore.getCatalogEntriesBySubscrition(
      selectedSubscription,
      selectedSubscription !== userStore.currentSubscriptionCode,
    );

    if (entries) {
      catalogStore.removeModalityFromCatalog([Modality.ExpertSeminar]);
      catalogStore.getFilterInfo();
      presetFilters();
      setCatalogUpdateInProgress(false);
      onSelectMyTeam(selectedSubscription);
    }
  };

  const renderCreateForMyself = () => (
    <div className="panel panel-default select-organization-team">
      <div className="panel-heading">
        <SVG src={UserIcon} />
      </div>
      <div className="panel-body">
        <Row>
          <Col className="col-sm form-sub-title">
            {t('I want to create a custom skill path for myself.')}
          </Col>
        </Row>
      </div>
      <div className="panel-footer">
        <Button
          className="blue-btn"
          isDisabled={
            !userStore.currentSubscriptionCode && !catalogStore.loaded
          }
          key={uuidv4()}
          onClick={() => onSelectMyself()}
        >
          {t('Create for myself')}
        </Button>
      </div>
    </div>
  );

  const renderSelectTargetSubscription = () => (
    <div className="panel panel-default select-organization-team">
      <div className="panel-heading">
        <SVG src={UsersIcon} />
      </div>
      <div className="panel-body">
        <Form className="cskp-form-select-org-team" horizontal id={uuidv4()}>
          <FormGroup>
            <Col className="col-sm form-sub-title">
              {t('I want to create a custom skill path for a team.')}
            </Col>
          </FormGroup>

          <FormGroup>
            <Col className="col-sm" componentClass={ControlLabel}>
              {t('Subscription Type:')}
            </Col>
            <Col className="col-sm">
              <Dropdown
                className="org-subscription-dropdown"
                id="org-subscription-dropdown"
                isOpen={isDropdownOpen}
                onOpenChange={setIsDropdownOpen}
                onSelect={() => setIsDropdownOpen(false)}
                toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                  <MenuToggle
                    isExpanded={isDropdownOpen}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    ref={toggleRef}
                    style={{ width: '100%' }}
                  >
                    {selectedSubscription
                      ? USER_SUBSCRIPTION_CODE[selectedSubscription]
                      : t('Select a Subscription Type')}
                  </MenuToggle>
                )}
              >
                <DropdownList>
                  {subscriptions.length > 0 ? (
                    subscriptions.map((sub) => (
                      <DropdownItem
                        key={sub}
                        onClick={() => setSelectedSubscription(sub)}
                      >
                        {USER_SUBSCRIPTION_CODE[sub]}
                      </DropdownItem>
                    ))
                  ) : (
                    <DropdownItem isDisabled key="no-sub-in-team">
                      {isSubscriptionsLoading
                        ? t('Loading subscriptions under your team')
                        : t('No subscriptions exists under your team')}
                    </DropdownItem>
                  )}
                </DropdownList>
              </Dropdown>
            </Col>
          </FormGroup>
        </Form>
      </div>
      <div className="panel-footer">
        <Button
          className="blue-btn"
          isDisabled={selectedSubscription === null || catalogUpdateInProgress}
          key={uuidv4()}
          onClick={handleCreateForMyTeam}
        >
          {catalogUpdateInProgress ? (
            <span className="spinner-wrapper">
              <ViewLoader color="#ffffff" />
            </span>
          ) : (
            t('Create for my team')
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <Grid hasGutter>
      <GridItem md={3} sm={12}>
        <Button
          className="btn-block"
          key="lp-csk-button"
          onClick={() => routerStore.navigate('skills-paths')}
          variant="danger"
        >
          {t('Cancel')}
        </Button>
      </GridItem>
      <GridItem md={4} sm={12}>
        {renderSelectTargetSubscription()}
      </GridItem>
      {userStore.currentSubscriptionCode && (
        <GridItem md={4} sm={12}>
          {renderCreateForMyself()}
        </GridItem>
      )}
      <GridItem lg={1} />
    </Grid>
  );
};

export default observer(OrgLeaderCustomPathSelection);
